/** @format */
import { css } from 'styled-components';

import { mobile, smallTablet } from '@toucan/helpers/media-queries';

import { StyledSearchBarProps } from './index';
import {
    getDesktopTypeaheadSearchButtonDisplayValue,
    getSmallTabletSearchBarDisplayValue,
    getSmallTabletSearchButtonDisplayValue,
} from './style-helpers';

/**
 * Base styles for the search bar component
 */
const baseStyles = ({ hasSearchValue }: StyledSearchBarProps) => css`
    input {
        border: none;

        &:active,
        &:focus,
        &:hover {
            box-shadow: none;
            border: none;
        }

        &::placeholder {
            -webkit-text-fill-color: var(--colors-text-secondary);
            color: var(--colors-text-secondary);
        }
    }

    .typeahead label {
        display: none;
    }

    .search-bar {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background: var(--colors-surface-primary);
        z-index: 10000;
        min-height: 100%;
        display: flex;
    }

    .search-bar__wrapper {
        width: 100%;
    }

    .search-bar__inner {
        height: 100%;
        display: flex;
        align-items: center;
    }

    .search-bar__icon-container {
        position: absolute;
        top: 0;
        bottom: 0;
        pointer-events: none;
        overflow: hidden;
        width: 100%;
        left: 0;
        right: 0;

        > * {
            pointer-events: all;
        }
    }

    .search-bar__icon {
        height: 100%;
        display: flex;
        align-items: center;
        z-index: 11;
        position: relative;
        pointer-events: none;

        svg {
            width: 2.4rem;
            height: 2.4rem;
            fill: var(--colors-text-secondary);
        }
    }

    .typeahead-items,
    .typeahead__no-suggestions-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding-left: var(--layout-spacing-24);
        padding-right: var(--layout-spacing-24);
        padding-bottom: 3rem;

        ${mobile(css`
            padding-left: var(--layout-spacing-12);
            padding-right: var(--layout-spacing-12);
        `)}

        > * {
            max-width: 134.4rem;
            width: 100%;
        }
    }

    .search-button,
    .typeahead-search-button {
        display: flex;
        height: 4rem;
        width: 4rem;
        position: absolute;
        right: var(--layout-spacing-12);
        bottom: 50%;
        transform: translateY(50%);
        justify-content: center;
        align-items: center;
        border-radius: 50%;

        pointer-events: none;

        ${smallTablet(css`
            pointer-events: unset;
            height: 3.6rem;
            width: 3.6rem;
        `)}

        z-index: 5;
    }

    .search-button {
        display: none;
    }

    && .typeahead-search-button {
        display: ${getDesktopTypeaheadSearchButtonDisplayValue(hasSearchValue)};
    }

    .search-bar .input-clear {
        min-height: 4rem;
        min-width: 4rem;
        position: absolute;
        right: var(--layout-spacing-12);
        bottom: 50%;
        transform: translateY(50%);
        border: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
        background-color: var(--colors-text-primary);

        z-index: 5;

        svg {
            fill: var(--colors-text-inverse);
        }
    }

    .search-bar__typeahead {
        background-color: var(--colors-surface-primary);
    }

    .search-bar__typeahead input {
        background: transparent;
    }
`;

const smallTabletStyles = ({ isOpen }: StyledSearchBarProps) => css`
    && .typeahead-search-button {
        display: none;
    }

    .search-bar {
        display: ${getSmallTabletSearchBarDisplayValue(isOpen)};
    }

    .search-button {
        display: ${getSmallTabletSearchButtonDisplayValue(isOpen)};
    }
`;

/**
 * The search bar component styles
 *
 * @param {StyledSearchBarProps} props The search bar properties object
 * */
export const styles = (props: StyledSearchBarProps) => css`
    ${baseStyles(props)}
    ${smallTablet(smallTabletStyles(props))}
`;
